const STORAGE_KEY = 'url-params';

/**
 * Shows or hides its content by the URL parameters a visitor landed with, e.g. UTM tags. They are
 * kept for the rest of the visit, since the parameters are gone after the first click.
 *
 * Attributes take query-string syntax, `key=value&key=value`, and match only when every pair does.
 * A value is a case-insensitive prefix, and a bare `key` matches any value:
 * - `visible-params`: show only to visitors who match (render with `hidden`)
 * - `hidden-params`: hide from visitors who match
 *
 * Fires a bubbling `url-param-visibility:change` once decided, for a parent that lays out around it.
 */
class UrlParamVisibility extends HTMLElement {
  connectedCallback() {
    const params = this.#params();
    const visible = this.#entries('visible-params');
    const hidden = this.#entries('hidden-params');
    const matches = (/** @type {[string, string][]} */ entries) =>
      entries.length > 0 &&
      entries.every(([key, prefix]) => params[key]?.toUpperCase().startsWith(prefix.toUpperCase()));

    this.hidden = (visible.length > 0 && !matches(visible)) || matches(hidden);
    this.dispatchEvent(new Event('url-param-visibility:change', { bubbles: true }));
  }

  /** @returns {Record<string, string>} The parameters from this visit, the current URL winning. */
  #params() {
    const current = Object.fromEntries(new URLSearchParams(window.location.search));

    try {
      const params = { ...JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}'), ...current };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
      return params;
    } catch {
      // sessionStorage can be unavailable (private mode, blocked site data); fall back to the URL alone.
      return current;
    }
  }

  /** @param {string} name */
  #entries(name) {
    return [...new URLSearchParams((this.getAttribute(name) ?? '').trim().replace(/^\?/, ''))];
  }
}

if (!customElements.get('url-param-visibility')) {
  customElements.define('url-param-visibility', UrlParamVisibility);
}
